import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Cpu, Package, AlertTriangle, ClipboardList, Clock, ThumbsUp,
  CheckCircle2, ArrowRight,
} from 'lucide-react';
import { dashboardApi, reportsApi } from '@/lib/api';
import { Badge, statusColor, priorityColor } from '@/components/ui/badge';
import { Card, CardHeader, CardBody } from '@/components/ui/card';

const TASK_TYPE_COLORS: Record<string, string> = {
  corrective: '#ef4444',
  preventive: '#3b82f6',
  predictive: '#8b5cf6',
  unlinked:   '#94a3b8',
};

const BAR_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e', '#f97316', '#eab308'];

function fmtMonthShort(iso: string): string {
  const [year, month] = iso.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short' });
}

function ChartCardHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      <Link to="/reports" className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-0.5">
        Full report <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function ChartEmpty({ msg }: { msg: string }) {
  return <p className="text-center text-slate-400 text-xs py-8">{msg}</p>;
}

// ── KPI card ─────────────────────────────────────────────────────────────────

type KpiColor = 'indigo' | 'teal' | 'amber' | 'sky' | 'red' | 'violet';

interface KpiCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: KpiColor;
  to: string;
}

function KpiCard({ label, value, icon, color, to }: KpiCardProps) {
  const chipClass = {
    indigo: 'bg-brand-700 text-white',
    teal:   'bg-teal-700 text-white',
    amber:  'bg-amber-700 text-white',
    sky:    'bg-sky-700 text-white',
    red:    'bg-red-700 text-white',
    violet: 'bg-violet-700 text-white',
  }[color];
  const borderClass = {
    indigo: 'border-l-brand-600',
    teal:   'border-l-teal-600',
    amber:  'border-l-amber-600',
    sky:    'border-l-sky-600',
    red:    'border-l-red-600',
    violet: 'border-l-violet-600',
  }[color];

  return (
    <Link
      to={to}
      className={`rounded-lg border border-canvas-line border-l-4 ${borderClass} bg-white px-4 py-3.5 flex items-start gap-3 transition-colors cursor-pointer hover:bg-canvas-surface`}
    >
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0 ${chipClass}`}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-2xl font-semibold font-mono tracking-tight leading-none text-ink-900">{value}</p>
        <p className="text-[11px] font-medium uppercase tracking-wide text-ink-400 mt-1.5">{label}</p>
      </div>
    </Link>
  );
}

// ── Get-started empty state ───────────────────────────────────────────────────

interface Step {
  label: string;
  description: string;
  done: boolean;
  to: string;
  cta: string;
}

function GettingStarted({ steps }: { steps: Step[] }) {
  const first = steps.find(s => !s.done);
  const doneCount = steps.filter(s => s.done).length;
  const total = steps.length;
  const pct = Math.round((doneCount / total) * 100);

  return (
    <div className="rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 to-slate-50 shadow-[0_1px_0_#fff_inset,0_4px_24px_-8px_rgba(11,18,32,.06)] overflow-hidden">
      {/* Header */}
      <div className="px-8 pt-8 pb-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-brand-100 text-brand-700">
            Getting started
          </span>
          <span className="text-xs text-slate-500">~5 minutes</span>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Welcome to VantixMT</h2>
        <p className="text-sm text-slate-500">
          Four quick steps to get your maintenance operation up and running. Each one takes about a minute.
        </p>

        {/* Progress bar */}
        <div className="mt-5 flex items-center gap-3">
          <div className="flex-1 h-1.5 rounded-full bg-white/60 overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-600 transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs font-medium text-slate-600 mono whitespace-nowrap">
            {doneCount} of {total}
          </span>
        </div>
      </div>

      {/* Steps */}
      <ol className="divide-y divide-white/60 border-t border-white/60">
        {steps.map((step, i) => {
          const isCurrent = step === first;
          return (
            <li
              key={i}
              className={`flex items-center gap-4 px-8 py-4 transition-colors ${
                isCurrent ? 'bg-white/70' : ''
              }`}
            >
              {step.done ? (
                <CheckCircle2 size={22} className="text-emerald-500 flex-shrink-0" />
              ) : (
                <div className={`w-[22px] h-[22px] flex-shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  isCurrent ? 'bg-brand-600 text-white' : 'bg-white text-slate-400 border border-slate-200'
                }`}>
                  {i + 1}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${
                  step.done ? 'text-slate-400 line-through' :
                  isCurrent ? 'text-slate-900' : 'text-slate-700'
                }`}>
                  {step.label}
                </p>
                {!step.done && (
                  <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>
                )}
              </div>
              {isCurrent && (
                <Link
                  to={step.to}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-md px-3.5 py-2 transition-colors shadow-sm"
                >
                  {step.cta} <ArrowRight size={12} />
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn:  dashboardApi.get,
    refetchInterval: 5 * 60 * 1_000, // refresh every 5 minutes
  });

  const reportDates = React.useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 90);
    return { start_date: start.toISOString(), end_date: end.toISOString() };
  }, []);

  const { data: report } = useQuery({
    queryKey: ['dashboard', 'downtime-90d'],
    queryFn:  () => reportsApi.downtime(reportDates),
    refetchInterval: 5 * 60 * 1_000,
  });

  if (isLoading) {
    return <p className="py-20 text-center text-slate-400">Loading dashboard…</p>;
  }

  const kpis = data?.kpis;

  // Compute wizard step completion individually so the wizard persists until
  // ALL four steps are done, not just until the first piece of equipment is added.
  const totalWOs = Object.values(data?.wo_by_status ?? {}).reduce((a, b) => a + b, 0);
  const wizardSteps: Step[] = [
    {
      label:       'Add your first piece of equipment',
      description: 'Every maintenance task and work order is tied to an asset. Start here.',
      done:        (kpis?.total_equipment ?? 0) > 0,
      to:          '/equipment',
      cta:         'Add equipment',
    },
    {
      label:       'Add spare parts to your inventory',
      description: 'Track stock levels and get alerts when parts run low.',
      done:        (kpis?.total_parts ?? 0) > 0,
      to:          '/parts',
      cta:         'Add parts',
    },
    {
      label:       'Create a maintenance schedule',
      description: 'Set up recurring PM tasks — VantixMT will auto-generate work orders when they\'re due.',
      done:        (kpis?.total_schedules ?? 0) > 0,
      to:          '/schedules',
      cta:         'Create schedule',
    },
    {
      label:       'Open your first work order',
      description: 'Manually create a WO or let the schedule generate one for you.',
      done:        totalWOs > 0,
      to:          '/work-orders',
      cta:         'Create work order',
    },
  ];
  const wizardComplete = wizardSteps.every(s => s.done);

  if (!wizardComplete) {
    return (
      <div className="flex flex-col gap-6">
        <GettingStarted steps={wizardSteps} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard
          label="Equipment"
          value={kpis?.total_equipment ?? 0}
          icon={<Cpu size={20} />}
          color="indigo"
          to="/equipment"
        />
        <KpiCard
          label="Parts"
          value={kpis?.total_parts ?? 0}
          icon={<Package size={20} />}
          color="teal"
          to="/parts"
        />
        <KpiCard
          label="Low Stock"
          value={kpis?.low_stock_parts ?? 0}
          icon={<AlertTriangle size={20} />}
          color="amber"
          to="/parts"
        />
        <KpiCard
          label="Open WOs"
          value={kpis?.open_work_orders ?? 0}
          icon={<ClipboardList size={20} />}
          color="sky"
          to="/work-orders"
        />
        <KpiCard
          label="Overdue Tasks"
          value={kpis?.overdue_tasks ?? 0}
          icon={<Clock size={20} />}
          color="red"
          to="/schedules"
        />
        <KpiCard
          label="Awaiting Approval"
          value={kpis?.awaiting_approval ?? 0}
          icon={<ThumbsUp size={20} />}
          color="violet"
          to="/work-orders"
        />
      </div>

      {/* Work order status breakdown */}
      {data?.wo_by_status && Object.keys(data.wo_by_status).length > 0 && (
        <Card>
          <CardHeader title="Work Orders by Status" />
          <CardBody>
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.wo_by_status).map(([status, count]) => (
                <div key={status} className="flex items-center gap-2">
                  <Badge color={statusColor(status)}>{status.replace(/_/g, ' ')}</Badge>
                  <span className="text-sm font-semibold text-slate-700">{count}</span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Mini charts (last 90 days) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="overflow-hidden">
          <ChartCardHeader title="Downtime by Equipment (90d)" />
          <CardBody>
            {!report || report.by_equipment.length === 0 ? (
              <ChartEmpty msg="No downtime in the last 90 days." />
            ) : (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart
                  data={report.by_equipment.slice(0, 6).map(d => ({
                    name:  d.asset_tag || d.equipment_name,
                    hours: parseFloat((d.downtime_minutes / 60).toFixed(1)),
                  }))}
                  margin={{ top: 4, right: 8, left: -12, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} unit="h" />
                  <Tooltip formatter={(v: number) => [`${v}h`, 'Downtime']} contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="hours" radius={[3, 3, 0, 0]}>
                    {report.by_equipment.slice(0, 6).map((_e, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>

        <Card className="overflow-hidden">
          <ChartCardHeader title="By Work Type (90d)" />
          <CardBody>
            {!report || report.by_task_type.length === 0 ? (
              <ChartEmpty msg="No data." />
            ) : (
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={report.by_task_type.map(d => ({ name: d.task_type, value: d.downtime_minutes }))}
                    cx="50%" cy="44%" innerRadius={38} outerRadius={58} paddingAngle={3} dataKey="value"
                  >
                    {report.by_task_type.map((d, i) => (
                      <Cell key={i} fill={TASK_TYPE_COLORS[d.task_type] ?? '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: number) => {
                      const h = Math.floor(v / 60); const m = v % 60;
                      return [h > 0 ? `${h}h ${m}m` : `${m}m`, 'Downtime'];
                    }}
                    contentStyle={{ fontSize: 11 }}
                  />
                  <Legend
                    iconSize={10}
                    verticalAlign="bottom"
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(value: string) => value.charAt(0).toUpperCase() + value.slice(1)}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>

        <Card className="overflow-hidden">
          <ChartCardHeader title="Monthly WO Trend" />
          <CardBody>
            {!report || report.by_month.length < 2 ? (
              <ChartEmpty msg="Needs ≥2 months of data." />
            ) : (
              <ResponsiveContainer width="100%" height={170}>
                <LineChart
                  data={report.by_month.map(d => ({
                    month: fmtMonthShort(d.month),
                    incidents: d.incidents,
                  }))}
                  margin={{ top: 4, right: 12, left: -16, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip formatter={(v: number) => [v, 'Incidents']} contentStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="incidents" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent work orders */}
        <Card>
          <CardHeader title="Recent Work Orders" />
          <CardBody className="p-0">
            {data?.recent_work_orders.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-400">No work orders yet.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data?.recent_work_orders.map(wo => (
                    <tr
                      key={wo.id}
                      className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">
                        {wo.wo_number}
                      </td>
                      <td className="px-2 py-3 font-medium max-w-[10rem] truncate">{wo.title}</td>
                      <td className="px-2 py-3 text-xs text-slate-700">
                        {wo.equipment?.name ?? wo.custom_equipment_name ?? '—'}
                      </td>
                      <td className="px-2 py-3">
                        <Badge color={statusColor(wo.status)}>{wo.status.replace(/_/g, ' ')}</Badge>
                      </td>
                      <td className="px-2 py-3">
                        <Badge color={priorityColor(wo.priority)}>{wo.priority}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>

        {/* Upcoming maintenance tasks */}
        <Card>
          <CardHeader title="Upcoming Maintenance" />
          <CardBody className="p-0">
            {data?.upcoming_tasks.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-400">No upcoming tasks.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data?.upcoming_tasks.map(task => (
                    <tr
                      key={task.id}
                      className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate('/schedules')}
                    >
                      <td className="px-4 py-3 font-medium max-w-[12rem] truncate">{task.name}</td>
                      <td className="px-2 py-3 text-xs text-slate-700">
                        {task.equipment?.name ?? '—'}
                      </td>
                      <td className="px-2 py-3 text-xs text-slate-600 whitespace-nowrap">
                        {task.next_due_at
                          ? new Date(task.next_due_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-2 py-3">
                        <Badge color="blue">{task.task_type}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
